// A board card, as the API returns it: no column or order is worked out here.
// The card is an <article> with one link stretched over it (the record), so the chips and the
// batch badge inside it can be links of their own (an <a> may not hold another).
// Hook classes (no styles of their own; tests and the browser check select on them): card,
// card-top, id, batch-group, batch-bugs, num; a plan's owner is the `tag owner`.
import { Link } from 'react-router-dom';
import type { Card, IssueSummary, PullSummary } from '../api/client';
import { useAgent } from '../lib/agents';
import { useLabelRoles } from '../lib/labels';
import { recordPath } from '../lib/routes';
import { BadgeTag, ChecksSummary, EpicTag, KIND_ICON, KIND_NAME, LabelTag, ReviewText, prStateText, tagClass } from './Bits';
import { AgentChip } from './Fleet';
import { Icon } from './Icon';
import { cn, mono } from './ui';

const TITLE = 'mb-0 mt-[5px] font-medium leading-[1.38] text-ink-display [overflow-wrap:anywhere]';
const SRC = 'max-w-full truncate font-mono text-[10.5px] text-ink-muted';
const META = 'mt-2 flex min-w-0 flex-wrap items-center gap-1.5 text-[11.5px] text-ink-muted';

function refChip(state: 'open' | 'closed' | 'static'): string {
  return cn(
    'inline-flex h-[22px] items-center whitespace-nowrap rounded-[5px] border px-[7px] font-mono text-[11px] font-medium coarse:h-8 coarse:min-w-10 coarse:justify-center',
    state === 'static' ? 'border-dashed border-hairline-strong text-ink-muted' : 'border-hairline-strong',
    state === 'open' && 'text-ink-display',
    state === 'closed' && 'text-ink-muted line-through',
    state !== 'static' && 'hover:border-accent-display hover:text-accent-display hover:no-underline',
  );
}

export function cardKey(c: Card): string {
  return `${c.meta_repo.id}:${c.key}`;
}

function PrRow({ pr }: { pr: PullSummary }) {
  return (
    <div className="mt-[9px] flex min-w-0 flex-wrap items-center gap-x-[7px] gap-y-1 border-t border-dashed border-hairline pt-2 text-[11.5px] text-ink-muted">
      <Icon name={pr.state === 'merged' ? 'merge' : 'pr'} small />
      <span className="font-mono font-medium text-ink-display">#{pr.number}</span>
      {pr.state === 'open' ? (
        <ChecksSummary checks={pr.checks} />
      ) : (
        <span className={pr.state === 'merged' ? 'text-checks-pass-ink' : 'text-ink-muted'}>{prStateText(pr)}</span>
      )}
      <span className="w-full basis-full text-[11px] text-ink-muted" aria-label="Production deployment">
        {pr.production.deployed ? 'Deployed to production' : pr.production.reported
          ? `Production: ${pr.production.attempt_state ?? 'reported'}${pr.production.active_sha ? ' · active version available' : ''}`
          : 'No deployment reported'}
      </span>
      {pr.state === 'open' && <ReviewText review={pr.review} className="ml-auto" />}
    </div>
  );
}

/** A batch's bug, as a chip linking to the issue on GitHub. */
function IssueChip({ issue }: { issue: IssueSummary }) {
  const closed = issue.state === 'closed';
  return (
    <a
      className={refChip(closed ? 'closed' : 'open')}
      href={issue.url}
      target="_blank"
      rel="noopener noreferrer"
      title={issue.title}
      aria-label={`#${issue.number}${closed ? ' (closed)' : ''} on GitHub: ${issue.title}`}
    >
      #{issue.number}
    </a>
  );
}

/** A batch's bugs (unless listed beneath the card) and chores, as chips that wrap. */
function BatchChips({ card, ws, bugs }: { card: Card; ws: number; bugs: boolean }) {
  const issues = bugs ? card.issues : [];
  if (issues.length === 0 && card.chore_refs.length === 0) return null;
  return (
    <ul className="relative z-[1] m-0 mt-2 flex w-fit min-w-0 max-w-full list-none flex-wrap gap-1 p-0" aria-label={`In ${card.id}`}>
      {issues.map((i) => (
        <li key={i.number}>
          <IssueChip issue={i} />
        </li>
      ))}
      {card.chore_refs.map((c) => (
        <li key={c.id}>
          {c.key ? (
            <Link
              className={refChip('open')}
              to={recordPath(ws, card.meta_repo.id, c.key)}
              title={c.title ?? undefined}
              aria-label={`Chore ${c.id}${c.title ? `: ${c.title}` : ''}`}
            >
              {c.id}
            </Link>
          ) : (
            <span className={refChip('static')} title="No chore file with this ID">
              {c.id}
            </span>
          )}
        </li>
      ))}
    </ul>
  );
}

/** A batch's bugs beneath its card (the Bugs filter): one compact row each, linking to the issue. */
export function BatchBugs({ card }: { card: Card }) {
  if (card.issues.length === 0) return null;
  return (
    <ul
      className="batch-bugs m-0 ml-3.5 flex min-w-0 list-none flex-col gap-0.5 border-l-2 border-hairline-strong p-0 pl-2"
      aria-label={`Bugs in ${card.id}`}
      data-batch={card.key}
    >
      {card.issues.map((i) => (
        <li key={i.number}>
          <a
            href={i.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex min-w-0 items-baseline gap-2 rounded px-2 py-[5px] text-[12px] text-ink-body hover:bg-surface hover:no-underline coarse:min-h-10 coarse:items-center"
          >
            <span className="num flex-none font-mono text-[11px] font-medium text-ink-display">#{i.number}</span>
            <span className="min-w-0 flex-1 truncate">{i.title}</span>
            <span className="flex-none text-[11px] text-ink-muted">{i.state}</span>
          </a>
        </li>
      ))}
    </ul>
  );
}

/** `bugsBeneath`: the batch's bugs are listed under the card (BatchBugs), so no bug chips. */
export function CardView({
  card,
  ws,
  showRepo,
  bugsBeneath,
}: {
  card: Card;
  ws: number;
  showRepo?: boolean;
  bugsBeneath?: boolean;
}) {
  const unparsed = card.kind === 'unparsed';
  const isPr = card.kind === 'pr';
  const roles = useLabelRoles(card.meta_repo.id);
  const pr = isPr ? card.prs[0] : undefined;
  const otherBadges = card.badges.filter((b) => !(unparsed && b === 'unparsed'));
  const agent = useAgent(card.meta_repo.id, card.key);
  return (
    <article
      className={cn(
        'card relative block w-full min-w-0 rounded-lg border bg-page px-3 pb-[11px] pt-2.5 text-left text-ink-body hover:border-hairline-strong hover:bg-surface',
        unparsed ? 'border-dashed border-checks-fail' : 'border-hairline',
      )}
      data-kind={card.kind}
      data-key={card.key}
    >
      <Link
        to={recordPath(ws, card.meta_repo.id, card.key)}
        className="absolute inset-0 z-0 rounded-[inherit] focus-visible:rounded-[inherit] focus-visible:outline-offset-1"
        aria-label={`${KIND_NAME[card.kind]} ${card.id}: ${card.title}`}
      />
      <div className="card-top flex min-h-[18px] flex-wrap items-center gap-1.5 text-[11.5px] text-ink-muted">
        <span className="inline-flex text-ink-muted" title={KIND_NAME[card.kind]}>
          <Icon name={KIND_ICON[card.kind]} small />
        </span>
        {unparsed ? (
          <BadgeTag badge="unparsed" />
        ) : card.kind === 'inbox' ? (
          <span className="id whitespace-nowrap font-mono text-[11.5px] font-medium text-ink-muted" title={card.id}>
            {card.created ?? card.id}
          </span>
        ) : (
          <span className={cn('id whitespace-nowrap font-mono text-[11.5px] font-semibold', isPr ? 'text-accent-display' : 'text-ink-display')}>
            {card.id}
          </span>
        )}
        <span className="ml-auto inline-flex flex-wrap items-center justify-end gap-1">
          {card.batch && (
            <Link
              className={tagClass('badge-batch')}
              to={recordPath(ws, card.meta_repo.id, card.batch.key)}
              aria-label={`Batch ${card.batch.id}`}
              data-batch={card.batch.key}
            >
              <Icon name="batch" small />
              {card.batch.id}
            </Link>
          )}
          {card.epic && <EpicTag epic={card.epic} />}
          {card.owner && (
            <span className={tagClass('owner')} title="Owner" aria-label={`Owner ${card.owner}`}>
              {card.owner}
            </span>
          )}
          {otherBadges.map((b) => (
            <BadgeTag key={b} badge={b} />
          ))}
          {isPr && <span className={tagClass('kind')}>unlinked</span>}
        </span>
      </div>

      {unparsed ? (
        <>
          <p className={cn(TITLE, 'font-mono text-[12px]')}>{card.path ?? card.title}</p>
          {card.error && <p className="mb-0 mt-1.5 text-[11.5px]">{card.error}</p>}
        </>
      ) : (
        <p className={cn(TITLE, 'text-[13px]')}>{card.title}</p>
      )}

      {card.kind === 'batch' && <BatchChips card={card} ws={ws} bugs={!bugsBeneath} />}
      {(card.labels.length > 0 || card.warnings.length > 0 || showRepo) && (
        <div className={META}>
          {card.labels.map((l) => (
            <LabelTag key={l} name={l} roles={roles} />
          ))}
          {card.warnings.length > 0 && (
            <span className="inline-flex items-center gap-1 text-checks-pending-ink">
              <Icon name="warn" small />
              {card.warnings.length === 1 ? '1 warning' : `${card.warnings.length} warnings`}
            </span>
          )}
          {showRepo && <span className={SRC}>{card.meta_repo.full_name}</span>}
        </div>
      )}

      {pr ? (
        <>
          <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1 font-mono text-[10.5px] text-ink-muted [overflow-wrap:anywhere]">
            <Icon name="branch" small />
            <span className={mono}>{pr.head}</span>
            <Icon name="arrow" small />
            <span className={mono}>{pr.base}</span>
          </div>
          <PrRow pr={pr} />
        </>
      ) : (
        <>
          {card.path && !unparsed && card.kind !== 'inbox' && (
            <div className={META}>
              <span className={SRC}>{card.path}</span>
            </div>
          )}
          {card.prs.map((p) => (
            <PrRow key={`${p.repo}#${p.number}`} pr={p} />
          ))}
        </>
      )}
      {agent && (
        <div className="mt-2">
          <AgentChip ws={ws} host={agent.host} state={agent.state} />
        </div>
      )}
    </article>
  );
}
